/**
 * Export utilities for trips, expenses, and bookings
 */

import type { Trip } from '@/services/trips';
import type { Expense } from '@/services/expenses';
import type { Booking } from '@/services/bookings';
import type { ItineraryItem } from '@/services/itinerary';
import type { TripBudgetSnapshot } from '@/types/budget';
import { formatCurrency, convertToBaseCurrency } from '@/utils/currency';
import * as FileSystem from '@/lib/fileSystemLegacy';
import * as Sharing from 'expo-sharing';
import * as Calendar from 'expo-calendar';
import { Alert, Platform } from 'react-native';
import { copyToClipboard } from '@/utils/clipboard';

/**
 * Export expenses to CSV format
 */
export const exportExpensesToCSV = async (
  trip: Trip,
  expenses: Expense[],
  bookings: Booking[]
): Promise<void> => {
  try {
    const currency = trip.baseCurrency || 'USD';
    const fileName = `${trip.name.replace(/[^a-z0-9]/gi, '_')}_expenses_${new Date().toISOString().split('T')[0]}.csv`;
    
    // CSV Headers
    const headers = [
      'Date',
      'Description',
      'Category',
      'Amount',
      'Currency',
      'Location',
      'Merchant',
      'Payment Method',
      'Split',
      'Paid By',
    ];
    
    // Combine expenses and bookings
    const allItems: Array<{
      date: Date;
      description: string;
      category: string;
      amount: number;
      currency: string;
      location?: string;
      merchant?: string;
      paymentMethod?: string;
      split?: string;
      paidBy?: string;
    }> = [];
    
    // Add expenses
    expenses.forEach(expense => {
      let splitInfo = 'No';
      let paidBy = '';
      
      if (expense.split && expense.split.splitType !== 'none') {
        splitInfo = expense.split.splitType === 'equal' ? 'Equal split' : 'Custom split';
        // Get paid by user name if available
        paidBy = expense.split.paidBy || '';
      }
      
      allItems.push({
        date: expense.date,
        description: expense.description,
        category: expense.category,
        amount: expense.amount,
        currency: expense.currency,
        location: expense.location,
        merchant: expense.merchant,
        paymentMethod: expense.paymentMethod,
        split: splitInfo,
        paidBy,
      });
    });
    
    // Add bookings
    bookings.forEach(booking => {
      const category = booking.type === 'flight' ? 'flight' :
                      booking.type === 'hotel' ? 'lodging' :
                      booking.type === 'car' ? 'transport' :
                      booking.type === 'ferry' || booking.type === 'transportation' ? 'transport' :
                      booking.type === 'restaurant' ? 'food' :
                      booking.type === 'activity' ? 'activity' : 'other';
      
      const date = booking.departureDate || booking.checkInDate || booking.rawEmailDate || new Date();
      const description = `${booking.type} - ${booking.provider}${booking.confirmationNumber ? ` (${booking.confirmationNumber})` : ''}`;
      
      allItems.push({
        date,
        description,
        category,
        amount: booking.amount || 0,
        currency: booking.currency || currency,
        location: booking.location,
        merchant: booking.provider,
        paymentMethod: undefined,
        split: 'No',
        paidBy: '',
      });
    });
    
    // Sort by date
    allItems.sort((a, b) => a.date.getTime() - b.date.getTime());
    
    // Build CSV content
    const csvRows = [
      headers.join(','),
      ...allItems.map(item => {
        const row = [
          item.date.toISOString().split('T')[0],
          `"${item.description.replace(/"/g, '""')}"`,
          item.category,
          item.amount.toFixed(2),
          item.currency,
          item.location ? `"${item.location.replace(/"/g, '""')}"` : '',
          item.merchant ? `"${item.merchant.replace(/"/g, '""')}"` : '',
          item.paymentMethod || '',
          item.split,
          item.paidBy ? `"${item.paidBy.replace(/"/g, '""')}"` : '',
        ];
        return row.join(',');
      }),
    ];
    
    const csvContent = csvRows.join('\n');
    
    // Save and share file
    await saveAndShareFile(csvContent, fileName, 'text/csv');
    
    // Success alert is handled in saveAndShareFile
  } catch (error) {
    console.error('[Export] Error exporting expenses to CSV:', error);
    // Error alert is handled in saveAndShareFile
  }
};

/**
 * Generate iCal format for trip calendar export
 */
export const exportTripToiCal = async (
  trip: Trip,
  itineraryItems: ItineraryItem[]
): Promise<void> => {
  try {
    console.log('[Export] Starting iCal export for trip:', trip.name);
    const fileName = `${trip.name.replace(/[^a-z0-9]/gi, '_')}_calendar_${new Date().toISOString().split('T')[0]}.ics`;
    
    const isValidDate = (value: unknown): value is Date =>
      value instanceof Date && !Number.isNaN(value.getTime());

    // Helper to format date for iCal (YYYYMMDDTHHmmssZ)
    const formatiCalDate = (date: Date): string => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
    };

    const formatDateOnly = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    };

    const addDays = (date: Date, days: number): Date => {
      const next = new Date(date);
      next.setDate(next.getDate() + days);
      return next;
    };

    const parseTime = (time: string | undefined): { hours: number; minutes: number } | null => {
      if (!time) return null;
      const normalized = time.trim();
      if (!normalized || normalized.toLowerCase() === 'flexible') return null;

      const match = normalized.match(/^(\d{1,2}):(\d{2})(?:\s*([AaPp][Mm]))?$/);
      if (!match) return null;
      const rawHours = Number(match[1]);
      const minutes = Number(match[2]);
      if (!Number.isFinite(rawHours) || !Number.isFinite(minutes) || minutes < 0 || minutes > 59) {
        return null;
      }

      const meridiem = match[3]?.toLowerCase();
      if (!meridiem) {
        if (rawHours < 0 || rawHours > 23) return null;
        return { hours: rawHours, minutes };
      }

      if (rawHours < 1 || rawHours > 12) return null;
      const hours = rawHours % 12 + (meridiem === 'pm' ? 12 : 0);
      return { hours, minutes };
    };

    const applyTime = (date: Date, time: { hours: number; minutes: number } | null): Date => {
      const next = new Date(date);
      if (!time) {
        next.setHours(0, 0, 0, 0);
        return next;
      }
      next.setHours(time.hours, time.minutes, 0, 0);
      return next;
    };

    const escapeICalText = (value: string): string =>
      value
        .replace(/\\/g, '\\\\')
        .replace(/\r\n|\r|\n/g, '\\n')
        .replace(/,/g, '\\,')
        .replace(/;/g, '\\;');

    const foldICalLine = (line: string): string[] => {
      const MAX = 75;
      if (line.length <= MAX) return [line];
      const parts: string[] = [line.slice(0, MAX)];
      let rest = line.slice(MAX);
      while (rest.length > 0) {
        parts.push(` ${rest.slice(0, MAX - 1)}`);
        rest = rest.slice(MAX - 1);
      }
      return parts;
    };
    
    // Generate unique ID
    const uid = () => {
      return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}@tripvio.app`;
    };
    
    // Build iCal content
    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Tripvio//Trip Calendar Export//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
    ];
    
    // Add main trip event
    const safeTripStart = new Date(trip.startDate);
    const safeTripEnd = new Date(trip.endDate);
    const tripStart = isValidDate(safeTripStart) ? safeTripStart : new Date();
    const tripEnd = isValidDate(safeTripEnd) ? safeTripEnd : tripStart;
    const tripEndExclusive = addDays(tripEnd, 1);
    const tripUid = uid();
    
    const tripDescription = [
      trip.destination ? `Destination: ${trip.destination}` : '',
      trip.travelingFrom ? `Traveling from: ${trip.travelingFrom}` : '',
      trip.budget ? `Budget: ${formatCurrency(trip.budget, trip.baseCurrency || 'USD')}` : '',
    ].filter(Boolean).join('\n');
    
    lines.push(
      'BEGIN:VEVENT',
      `UID:${tripUid}`,
      `DTSTART;VALUE=DATE:${formatDateOnly(tripStart)}`,
      `DTEND;VALUE=DATE:${formatDateOnly(tripEndExclusive)}`,
      `SUMMARY:${escapeICalText(trip.name)}`,
      `DESCRIPTION:${escapeICalText(tripDescription)}`,
      trip.destination ? `LOCATION:${escapeICalText(trip.destination)}` : '',
      `DTSTAMP:${formatiCalDate(new Date())}`,
      'END:VEVENT'
    );
    
    // Add itinerary items as separate events
    itineraryItems.forEach(item => {
      const rawStartDate = new Date(item.startDate);
      if (!isValidDate(rawStartDate)) {
        console.warn('[Export] Skipping itinerary item with invalid start date:', item.id);
        return;
      }

      const startTime = parseTime(item.startTime);
      const endTime = parseTime(item.endTime);
      const rawEndDate = item.endDate ? new Date(item.endDate) : null;
      const hasValidEndDate = rawEndDate != null && isValidDate(rawEndDate);
      const itemLocation = item.location || item.address || '';

      const itemDescription = [
        item.description || '',
        itemLocation ? `Location: ${itemLocation}` : '',
      ].filter(Boolean).join('\n');

      lines.push('BEGIN:VEVENT');
      lines.push(`UID:${uid()}`);

      if (startTime) {
        const timedStart = applyTime(rawStartDate, startTime);
        let timedEnd: Date;
        if (hasValidEndDate && endTime) {
          timedEnd = applyTime(rawEndDate as Date, endTime);
        } else if (hasValidEndDate && !endTime) {
          timedEnd = applyTime(rawEndDate as Date, startTime);
        } else {
          timedEnd = new Date(timedStart);
          timedEnd.setHours(timedEnd.getHours() + 2);
        }
        if (timedEnd.getTime() <= timedStart.getTime()) {
          timedEnd = new Date(timedStart);
          timedEnd.setHours(timedEnd.getHours() + 2);
        }
        lines.push(`DTSTART:${formatiCalDate(timedStart)}`);
        lines.push(`DTEND:${formatiCalDate(timedEnd)}`);
      } else {
        const allDayStart = rawStartDate;
        const allDayEnd = hasValidEndDate ? (rawEndDate as Date) : allDayStart;
        lines.push(`DTSTART;VALUE=DATE:${formatDateOnly(allDayStart)}`);
        lines.push(`DTEND;VALUE=DATE:${formatDateOnly(addDays(allDayEnd, 1))}`);
      }

      lines.push(`SUMMARY:${escapeICalText(item.title)}`);
      if (itemDescription) {
        lines.push(`DESCRIPTION:${escapeICalText(itemDescription)}`);
      }
      if (itemLocation) {
        lines.push(`LOCATION:${escapeICalText(itemLocation)}`);
      }
      lines.push(`DTSTAMP:${formatiCalDate(new Date())}`);
      lines.push('END:VEVENT');
    });
    
    lines.push('END:VCALENDAR');
    
    const icalContent = lines
      .filter(Boolean)
      .flatMap((line) => foldICalLine(line))
      .join('\r\n');
    
    // Save and share file
    await saveAndShareFile(icalContent, fileName, 'text/calendar');
    
    // Success alert is handled in saveAndShareFile
  } catch (error) {
    console.error('[Export] Error exporting trip to iCal:', error);
    // Error alert is handled in saveAndShareFile
  }
};

/**
 * Add trip + itinerary directly to native device calendar.
 */
export const addTripToDeviceCalendar = async (
  trip: Trip,
  itineraryItems: ItineraryItem[]
): Promise<void> => {
  try {
    if (Platform.OS === 'web') {
      Alert.alert('Not Available', 'Direct calendar add is only available on iOS and Android.');
      return;
    }

    const parseTime = (time: string | undefined): { hours: number; minutes: number } | null => {
      if (!time) return null;
      const normalized = time.trim();
      if (!normalized || normalized.toLowerCase() === 'flexible') return null;
      const match = normalized.match(/^(\d{1,2}):(\d{2})(?:\s*([AaPp][Mm]))?$/);
      if (!match) return null;
      const rawHours = Number(match[1]);
      const minutes = Number(match[2]);
      if (!Number.isFinite(rawHours) || !Number.isFinite(minutes) || minutes < 0 || minutes > 59) {
        return null;
      }
      const meridiem = match[3]?.toLowerCase();
      if (!meridiem) {
        if (rawHours < 0 || rawHours > 23) return null;
        return { hours: rawHours, minutes };
      }
      if (rawHours < 1 || rawHours > 12) return null;
      return { hours: (rawHours % 12) + (meridiem === 'pm' ? 12 : 0), minutes };
    };

    const addDays = (date: Date, days: number): Date => {
      const next = new Date(date);
      next.setDate(next.getDate() + days);
      return next;
    };

    const applyTime = (date: Date, time: { hours: number; minutes: number } | null): Date => {
      const next = new Date(date);
      if (!time) {
        next.setHours(0, 0, 0, 0);
        return next;
      }
      next.setHours(time.hours, time.minutes, 0, 0);
      return next;
    };

    const isValidDate = (value: unknown): value is Date =>
      value instanceof Date && !Number.isNaN(value.getTime());

    const { status: existingStatus } = await Calendar.getCalendarPermissionsAsync();
    let permissionStatus = existingStatus;
    if (permissionStatus !== 'granted') {
      const { status } = await Calendar.requestCalendarPermissionsAsync();
      permissionStatus = status;
    }
    if (permissionStatus !== 'granted') {
      Alert.alert('Permission Required', 'Calendar permission is required to add trip events.');
      return;
    }

    const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);
    const writableCalendar =
      calendars.find((cal) => cal.allowsModifications) ||
      calendars.find((cal) => cal.accessLevel === Calendar.CalendarAccessLevel.OWNER) ||
      calendars[0];

    if (!writableCalendar?.id) {
      Alert.alert('Calendar Unavailable', 'No editable calendar was found on this device.');
      return;
    }

    const createdEventIds: string[] = [];
    const safeTripStart = new Date(trip.startDate);
    const safeTripEnd = new Date(trip.endDate);
    const tripStart = isValidDate(safeTripStart) ? safeTripStart : new Date();
    const tripEnd = isValidDate(safeTripEnd) ? safeTripEnd : tripStart;
    const tripDescription = [
      trip.destination ? `Destination: ${trip.destination}` : '',
      trip.travelingFrom ? `Traveling from: ${trip.travelingFrom}` : '',
      trip.budget ? `Budget: ${formatCurrency(trip.budget, trip.baseCurrency || 'USD')}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    const tripEventId = await Calendar.createEventAsync(writableCalendar.id, {
      title: trip.name,
      startDate: tripStart,
      endDate: addDays(tripEnd, 1),
      allDay: true,
      location: trip.destination || undefined,
      notes: tripDescription || undefined,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    createdEventIds.push(tripEventId);

    for (const item of itineraryItems) {
      const rawStartDate = new Date(item.startDate);
      if (!isValidDate(rawStartDate)) continue;

      const startTime = parseTime(item.startTime);
      const endTime = parseTime(item.endTime);
      const rawEndDate = item.endDate ? new Date(item.endDate) : null;
      const hasValidEndDate = rawEndDate != null && isValidDate(rawEndDate);
      const location = item.location || item.address || undefined;
      const notes = [item.description || '', location ? `Location: ${location}` : '']
        .filter(Boolean)
        .join('\n');

      const eventInput: Omit<Partial<Calendar.Event>, 'id' | 'organizer'> = {
        title: item.title,
        location: location ?? null,
        notes: notes || '',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      if (startTime) {
        const timedStart = applyTime(rawStartDate, startTime);
        let timedEnd: Date;
        if (hasValidEndDate && endTime) {
          timedEnd = applyTime(rawEndDate as Date, endTime);
        } else if (hasValidEndDate) {
          timedEnd = applyTime(rawEndDate as Date, startTime);
        } else {
          timedEnd = new Date(timedStart);
          timedEnd.setHours(timedEnd.getHours() + 2);
        }
        if (timedEnd.getTime() <= timedStart.getTime()) {
          timedEnd = new Date(timedStart);
          timedEnd.setHours(timedEnd.getHours() + 2);
        }
        eventInput.startDate = timedStart;
        eventInput.endDate = timedEnd;
        eventInput.allDay = false;
      } else {
        const allDayStart = rawStartDate;
        const allDayEnd = hasValidEndDate ? (rawEndDate as Date) : rawStartDate;
        eventInput.startDate = allDayStart;
        eventInput.endDate = addDays(allDayEnd, 1);
        eventInput.allDay = true;
      }

      try {
        const eventId = await Calendar.createEventAsync(writableCalendar.id, eventInput);
        createdEventIds.push(eventId);
      } catch (eventError) {
        console.warn('[Export] Failed to add itinerary event to native calendar', {
          itemId: item.id,
          eventError,
        });
      }
    }

    Alert.alert(
      'Added to Calendar',
      `Added ${createdEventIds.length} events to "${writableCalendar.title}".`
    );
  } catch (error) {
    console.error('[Export] Error adding trip to device calendar:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    Alert.alert('Calendar Error', `Could not add events to your calendar: ${errorMessage}`);
    throw error;
  }
};

/**
 * Export trip summary to HTML (can be converted to PDF)
 */
export const exportTripSummaryToHTML = async (
  trip: Trip,
  expenses: Expense[],
  bookings: Booking[],
  itineraryItems: ItineraryItem[],
  budgetSnapshot?: TripBudgetSnapshot,
): Promise<void> => {
  try {
    console.log('[Export] Starting HTML export for trip:', trip.name);
    const fileName = `${trip.name.replace(/[^a-z0-9]/gi, '_')}_summary_${new Date().toISOString().split('T')[0]}.html`;
    const currency = trip.baseCurrency || 'USD';

    const snapshotTotal =
      budgetSnapshot != null
        ? (budgetSnapshot.expensesConverted || 0) + (budgetSnapshot.bookingsConverted || 0)
        : 0;

    /** Match BudgetSummary: prefer converted snapshot when non-zero; else sum props with FX. */
    let totalSpent: number;
    if (budgetSnapshot != null && snapshotTotal > 0) {
      totalSpent = snapshotTotal;
    } else {
      const totalsByCurrency: Record<string, number> = {};
      for (const exp of expenses) {
        if (!exp.amount || Number.isNaN(exp.amount)) continue;
        const c = (exp.currency || currency).toUpperCase();
        totalsByCurrency[c] = (totalsByCurrency[c] || 0) + exp.amount;
      }
      for (const book of bookings) {
        if (!book.amount || Number.isNaN(book.amount)) continue;
        const c = (book.currency || currency).toUpperCase();
        totalsByCurrency[c] = (totalsByCurrency[c] || 0) + book.amount;
      }
      const entries = Object.entries(totalsByCurrency);
      if (entries.length === 0) {
        totalSpent = 0;
      } else if (entries.length === 1 && entries[0][0] === currency.toUpperCase()) {
        totalSpent = entries[0][1];
      } else {
        totalSpent = await convertToBaseCurrency(
          entries.map(([curr, amount]) => ({ amount, currency: curr })),
          currency,
        );
      }
    }

    const totalExpenses = expenses.reduce(
      (sum, exp) => sum + (exp.amount && !Number.isNaN(exp.amount) ? exp.amount : 0),
      0,
    );
    const totalBookings = bookings.reduce(
      (sum, book) => sum + (book.amount && !Number.isNaN(book.amount) ? book.amount : 0),
      0,
    );
    
    // Format dates
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    };
    
    // Build HTML content
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Trip Summary: ${trip.name}</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
    }
    h1 { color: #2c3e50; border-bottom: 3px solid #3498db; padding-bottom: 10px; }
    h2 { color: #34495e; margin-top: 30px; }
    .section { margin: 20px 0; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin: 15px 0; }
    .info-item { padding: 10px; background: #f8f9fa; border-radius: 5px; }
    .info-label { font-weight: 600; color: #7f8c8d; font-size: 0.9em; }
    .info-value { font-size: 1.1em; margin-top: 5px; }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
    th { background: #3498db; color: white; }
    tr:hover { background: #f5f5f5; }
    .total { font-weight: bold; font-size: 1.2em; }
    .budget-warning { color: #e74c3c; }
    .budget-good { color: #27ae60; }
  </style>
</head>
<body>
  <h1>${trip.name}</h1>
  
  <div class="section">
    <h2>Trip Information</h2>
    <div class="info-grid">
      <div class="info-item">
        <div class="info-label">Destination</div>
        <div class="info-value">${trip.destination || 'Not specified'}</div>
      </div>
      <div class="info-item">
        <div class="info-label">Traveling From</div>
        <div class="info-value">${trip.travelingFrom || 'Not specified'}</div>
      </div>
      <div class="info-item">
        <div class="info-label">Start Date</div>
        <div class="info-value">${formatDate(trip.startDate)}</div>
      </div>
      <div class="info-item">
        <div class="info-label">End Date</div>
        <div class="info-value">${formatDate(trip.endDate)}</div>
      </div>
    </div>
  </div>
  
  <div class="section">
    <h2>Budget Summary</h2>
    <div class="info-grid">
      <div class="info-item">
        <div class="info-label">Budget</div>
        <div class="info-value">${trip.budget ? formatCurrency(trip.budget, currency) : 'Not set'}</div>
      </div>
      <div class="info-item">
        <div class="info-label">Total Spent</div>
        <div class="info-value ${trip.budget && totalSpent > trip.budget ? 'budget-warning' : 'budget-good'}">
          ${formatCurrency(totalSpent, currency)}
        </div>
      </div>
      ${trip.budget ? `
      <div class="info-item">
        <div class="info-label">Remaining</div>
        <div class="info-value ${trip.budget - totalSpent < 0 ? 'budget-warning' : 'budget-good'}">
          ${formatCurrency(trip.budget - totalSpent, currency)}
        </div>
      </div>
      <div class="info-item">
        <div class="info-label">Percentage Used</div>
        <div class="info-value ${(totalSpent / trip.budget * 100) > 90 ? 'budget-warning' : 'budget-good'}">
          ${(totalSpent / trip.budget * 100).toFixed(1)}%
        </div>
      </div>
      ` : ''}
    </div>
  </div>
  
  ${expenses.length > 0 ? `
  <div class="section">
    <h2>Expenses (${expenses.length})</h2>
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>Description</th>
          <th>Category</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        ${expenses.map(exp => `
        <tr>
          <td>${formatDate(exp.date)}</td>
          <td>${exp.description}</td>
          <td>${exp.category}</td>
          <td>${formatCurrency(exp.amount, exp.currency)}</td>
        </tr>
        `).join('')}
        <tr class="total">
          <td colspan="3">Total Expenses</td>
          <td>${formatCurrency(totalExpenses, currency)}</td>
        </tr>
      </tbody>
    </table>
  </div>
  ` : ''}
  
  ${bookings.length > 0 ? `
  <div class="section">
    <h2>Bookings (${bookings.length})</h2>
    <table>
      <thead>
        <tr>
          <th>Type</th>
          <th>Provider</th>
          <th>Location</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        ${bookings.map(book => `
        <tr>
          <td>${book.type}</td>
          <td>${book.provider}</td>
          <td>${book.location || 'N/A'}</td>
          <td>${book.amount ? formatCurrency(book.amount, book.currency || currency) : 'N/A'}</td>
        </tr>
        `).join('')}
        <tr class="total">
          <td colspan="3">Total Bookings</td>
          <td>${formatCurrency(totalBookings, currency)}</td>
        </tr>
      </tbody>
    </table>
  </div>
  ` : ''}
  
  ${itineraryItems.length > 0 ? `
  <div class="section">
    <h2>Itinerary (${itineraryItems.length} items)</h2>
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>Title</th>
          <th>Category</th>
          <th>Location</th>
        </tr>
      </thead>
      <tbody>
        ${itineraryItems.map(item => `
        <tr>
          <td>${formatDate(item.startDate)}${item.startTime ? ` ${item.startTime}` : ''}</td>
          <td>${item.title}</td>
          <td>${item.category}</td>
          <td>${item.location || item.address || 'N/A'}</td>
        </tr>
        `).join('')}
      </tbody>
    </table>
  </div>
  ` : ''}
  
  <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #ddd; text-align: center; color: #7f8c8d; font-size: 0.9em;">
    Generated by Tripvio on ${new Date().toLocaleString()}
  </div>
</body>
</html>`;
    
    // Save and share file
    await saveAndShareFile(html, fileName, 'text/html');
    
    // Success alert is handled in saveAndShareFile
  } catch (error) {
    console.error('[Export] Error exporting trip summary:', error);
    // Error alert is handled in saveAndShareFile
  }
};

/**
 * Generate shareable trip link
 */
export const generateShareableTripLink = (tripId: string): string => {
  // For now, return a deep link format
  // In production, this would generate a public shareable link via a Cloud Function
  const baseUrl = 'https://app.tripvio.app/trip';
  return `${baseUrl}/${tripId}`;
};

/**
 * Copy shareable link to clipboard
 */
export const copyShareableLink = async (tripId: string): Promise<void> => {
  try {
    const link = generateShareableTripLink(tripId);
    await copyToClipboard(link);
    
    // Log trip_shared event
    const { logAnalyticsEvent } = await import('@/services/analyticsLogger');
    logAnalyticsEvent('trip_shared', {
      tripId,
      platform: 'link',
      method: 'copy',
    }).catch((error) => {
      // Silently fail analytics - don't break sharing
      console.warn('[export] Failed to log trip_shared event', error);
    });
    
    Alert.alert('Link Copied', 'Trip link copied to clipboard');
  } catch (error) {
    console.error('Error copying link:', error);
    Alert.alert('Error', 'Failed to copy link. Please try again.');
  }
};

/**
 * Helper function to save and share file
 */
const saveAndShareFile = async (
  content: string,
  fileName: string,
  mimeType: string
): Promise<void> => {
  try {
    // Web platform: use blob download
    if (Platform.OS === 'web') {
      if (typeof document !== 'undefined' && typeof Blob !== 'undefined' && typeof URL !== 'undefined') {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        Alert.alert('Export Successful', `File "${fileName}" downloaded successfully.`);
        return;
      } else {
        throw new Error('Web APIs not available');
      }
    }

    // Native platforms: use FileSystem and Sharing
    const documentDir = FileSystem.documentDirectory;
    if (!documentDir) {
      throw new Error('Document directory not available');
    }

    // Clean filename to ensure it's valid
    const cleanFileName = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
    const fileUri = `${documentDir}${cleanFileName}`;
    
    console.log('[Export] Writing file to:', fileUri);
    
    // Write file
    await FileSystem.writeAsStringAsync(fileUri, content, {
      encoding: FileSystem.EncodingType.UTF8,
    });
    
    console.log('[Export] File written successfully');
    
    // Check if sharing is available
    const isAvailable = await Sharing.isAvailableAsync();
    console.log('[Export] Sharing available:', isAvailable);
    
    if (isAvailable) {
      try {
        await Sharing.shareAsync(fileUri, {
          mimeType,
          dialogTitle: `Share ${cleanFileName}`,
          UTI: mimeType, // iOS Universal Type Identifier
        });
        console.log('[Export] Share dialog opened');
      } catch (shareError: any) {
        console.error('[Export] Error sharing file:', shareError);
        // If user cancelled, don't show error
        if (shareError?.code === 'ERR_CANCELLED' || shareError?.message?.includes('cancelled')) {
          console.log('[Export] User cancelled sharing');
          return;
        }
        // Fallback: show file location
        Alert.alert(
          'File Saved',
          `File saved successfully.\n\nLocation: ${fileUri}\n\nYou can find it in your device's file manager.`,
          [{ text: 'OK' }]
        );
      }
    } else {
      // Fallback: show file location
      Alert.alert(
        'File Saved',
        `File saved successfully.\n\nLocation: ${fileUri}\n\nSharing is not available on this device. You can find the file in your device's file manager.`,
        [{ text: 'OK' }]
      );
    }
  } catch (error) {
    console.error('[Export] Error saving/sharing file:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    Alert.alert(
      'Export Error',
      `Failed to export file: ${errorMessage}\n\nPlease try again.`,
      [{ text: 'OK' }]
    );
    throw error;
  }
};

